import { styled } from "@mui/material";
import ErrorDialog from "controls/global/dialogs/error-dialog";
import * as UIModel from "entities/UIModel";
import { ActionResult } from "entities/UIModel";
import PDFObject from "pdfobject";
import React, {
  useEffect,
  useState,
} from "react";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import { useDocument } from "utils/context/DocumentContext";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { useStewartAccess } from "utils/context/StewartAccessContext";
import useNavRedirect from "utils/custom-hooks/useNavRedirect";

const DivRoot = styled("div")({
  height: "100%",
  width: "100%",
});

type Props = {
  pdf: UIModel.PdfDocument;
  isTabPanel?: boolean;
  isReports?: boolean;
  isStaticFile?: boolean;
  reports?: UIModel.Reports;
};

type ErrorReport = {
  hasError: boolean;
  message: string;
};

const ShowPdf = ({ pdf, isTabPanel = false, isReports = false, isStaticFile = false, reports }: Props) => {
  const [, { getProfileSettings }] = useProfileSettingsCache();
  const [, { getReportsString }] = useStewartAccess();
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] =
    useAutomaticProgressDialogActions();
  const [reportStatus, setReportStatus] = useState<ErrorReport>({ hasError: false, message: '' });
  const { redirect } = useNavRedirect();
  const [, { getDocumentUrl }] = useDocument();
  const [currentPdf, setCurrentPdf] = useState<UIModel.PdfDocument>();
  
  useEffect(() => {
    const checkWithinTabPanel = async () => {
      if (isTabPanel) {
        const actionResult: ActionResult = await getDocumentUrl(
          pdf.fileId,
          pdf.productType,
          pdf.orderId,
          pdf.documentId,
          0
        );
        const updatedPdf:UIModel.PdfDocument = {...pdf};
        updatedPdf.dcoumentInfo = actionResult.documentInfo;
        setCurrentPdf(updatedPdf);
      }
      else {
        setCurrentPdf(pdf);
      }
    };

    checkWithinTabPanel();
  },[]);

  useEffect(() => {
    const showDocument = async () => {
      let pdfUrl = "";

      if (isReports && reports) {
        openAutomaticProgressDialog();
        const reportData = await getReportsString(reports);
        if (reportData && reportData.reportString && !reportData.error) {
          const pdfBlob = `data:application/pdf;headers=filename%3D${pdf.documentId};base64,${reportData.reportString}`;

          PDFObject.embed(pdfBlob, `#pdfDiv${pdf?.documentId}`, {
            pdfOpenParams: { view: "FitH" },
          });
        }
        closeAutomaticProgressDialog();

        if (reportData.error) {
          if (reportData?.error?.response?.status === 403) {
            await getProfileSettings();
            redirect("/403", null);
          } else {
            setReportStatus((prevState) => ({
              ...prevState,
              hasError: true,
              message: reportData.error?.response?.statusText,
            }));
          }
        }
      }
      else if (isStaticFile) {
          if (currentPdf) {
              let filename = currentPdf.fileName;
              if (!currentPdf.fileName.toLowerCase().endsWith(".pdf")) {
                  filename = `${filename}.pdf`;
              }
              pdfUrl = `/api/Documents/GetCPLRequestForm/?a=${encodeURIComponent(
                  currentPdf.dcoumentInfo!
              )}&fileName=${encodeURIComponent(filename)}`;

              PDFObject.embed(pdfUrl, `#pdfDiv${currentPdf?.documentId}`, {
                  pdfOpenParams: { view: "FitH" },
              });
          }
      }
      else {
        if (currentPdf) {
          let filename = currentPdf.fileName;
          if (!currentPdf.fileName.toLowerCase().endsWith(".pdf")) {
            filename = `${filename}.pdf`;
          }
          pdfUrl = `/api/Documents/GetPdf/?a=${encodeURIComponent(
            currentPdf.dcoumentInfo!
          )}&fileName=${encodeURIComponent(filename)}`;
          
          PDFObject.embed(pdfUrl, `#pdfDiv${currentPdf?.documentId}`, {
            pdfOpenParams: { view: "FitH" },
          });
        }
      }
    };
    
    showDocument();
  }, [currentPdf]);


  return (
    <>
      <ErrorDialog
        isOpen={reportStatus.hasError}
        confirmationMessage={reportStatus.message}
        onYes={() => {
          setReportStatus(prevState => ({ ...prevState, hasError: false }));
        }}
      />
      <DivRoot id={`pdfDiv${pdf.documentId}`} />
    </>
  );
};

export default ShowPdf;
